// RoleDialog.js - Dialog for creating/editing user roles
import {
  useState,
  useEffect,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Alert,
  CircularProgress
} from './SettingsCommon';

const RoleDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    active: true,
    pages: []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        label: data.label || '',
        description: data.description || '',
        active: data.active ?? true,
        pages: data.pages || []
      });
    } else {
      setFormData({
        name: '',
        label: '',
        description: '',
        active: true,
        pages: []
      });
    }
    setError(null);
  }, [data, open]);

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.label.trim()) {
      setError('Name and Label are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Role' : 'Add New Role'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Role Name (internal)"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            fullWidth
            required
            placeholder="e.g., sales_manager"
            helperText="Lowercase, no spaces (use underscores)"
            disabled={!!data?.id}
          />
          <TextField
            label="Display Label"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            fullWidth
            required
            placeholder="e.g., Sales Manager"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
            placeholder="Brief description of this role's responsibilities"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {data?.id ? 'Save Changes' : 'Create Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleDialog;
